import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { useCmsMegaMenuQuery } from '@/api/cms/cms';
import { Link } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { ResourceLocation } from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { isSameOrContains, stripAllWhitespace, useOnEscape } from '@/helpers';
import { DrawerMegaMenuItem } from '@/types/drawerMegaMenuItem';
import styles from './MegaMenu.module.scss';
import { MenuDropdown } from './MenuDropdown';

interface MegaMenuProps {
	className?: string;
}

export const MegaMenu: React.FunctionComponent<MegaMenuProps> = ({ className }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);
	const { data: cmsMegaMenu } = useCmsMegaMenuQuery(settings?.data, settings?.data.cmsSettings.cmsMegaMenu || '');
	const navigate = useNavigate();
	const location = useLocation();

	const baseUrl = settings?.data.segmentations.find((segment) => segment.isCurrentSegmentation)?.baseUrl || '';

	const itemRef = React.useRef<(HTMLLIElement | null)[]>([]);

	const [showDropdown, setShowDropdown] = React.useState<number | null>(null);
	const hoverDelay = 200;

	const toggleDropdown = React.useCallback((i): void => {
		setShowDropdown(i);
	}, []);

	const closeDropdown = React.useCallback(() => {
		setShowDropdown(null);
	}, []);

	const debouncedToggle = React.useRef(
		debounce((i) => {
			toggleDropdown(i);
		}, hoverDelay),
	);

	const handleMouseHover = (e: React.BaseSyntheticEvent, i: number, item: DrawerMegaMenuItem): void => {
		if (!item.children || item.children.length === 0) {
			return;
		}
		if (!itemRef.current[i] || itemRef.current[i] === null || 'ontouchstart' in window) {
			return;
		}

		if (e.type === 'mouseenter' && isSameOrContains(itemRef.current[i], e.target as Element)) {
			debouncedToggle.current(i);
		}

		if (e.type === 'mouseleave') {
			debouncedToggle.current(null);
		}
	};

	// Handle escape
	useOnEscape(closeDropdown, showDropdown !== null);

	React.useEffect(() => {
		if (!location.pathname) {
			return;
		}
		// Disable dropdown (and overlay) on route change
		toggleDropdown(null);
	}, [location.pathname, toggleDropdown]);

	const handleEnterPress = (e, i: number): void => {
		if (e.key !== 'Enter') {
			return;
		}

		// If dropdown is already open, let 'Enter' navigate to url
		if (showDropdown !== null) {
			toggleDropdown(null);
			return;
		}

		// Otherwise prevent navigation
		e.preventDefault();

		// If dropdown not open yet, open it
		if (showDropdown !== i) {
			toggleDropdown(i);
			return;
		}

		// Otherwise close dropdown
		toggleDropdown(null);
	};

	const handleTabbing = (e): void => {
		if (e.key !== 'Tab') {
			return;
		}

		// When tabbing to a link and dropdown is already open, close it
		if (showDropdown !== null) {
			toggleDropdown(null);
		}
	};

	const handleTouch = (e, i: number, route?: ResourceLocation): void => {
		e.preventDefault();

		// If dropdown open for current link, navigate to it
		if (showDropdown === i) {
			navigate(route?.externalRoute || '', { state: route });
			return;
		}

		// If dropdown not open yet, open it
		if (showDropdown !== i) {
			toggleDropdown(i);
			return;
		}
	};

	const isTrulyExternal = (link: string): boolean => {
		return /^(?:[a-z]+:)?\/\//i.test(link) && !link.includes(baseUrl);
	};

	const calculateTarget = (externalLink: string | undefined): string | undefined => {
		if (!externalLink) {
			return undefined;
		}
		if (baseUrl !== '' && isTrulyExternal(externalLink)) {
			return '_blank';
		}

		return undefined;
	};

	return (
		<nav
			className={classNames(styles.wrapper, className)}
			aria-label={translations?.data.shared.navigation.primary}
			role={'navigation'}
		>
			<ul className={styles.mainList} role={'menubar'}>
				{cmsMegaMenu?.map((item, index) => {
					const isActive = showDropdown === index;
					const ariaControlId = `aria-controls_${stripAllWhitespace(item.name || '')}`;

					return (
						<li
							className={styles.mainListItem}
							ref={(ref) => {
								itemRef.current[index] = ref;
							}}
							onMouseEnter={(e): void => {
								handleMouseHover(e, index, item);
							}}
							onMouseLeave={(e): void => {
								handleMouseHover(e, index, item);
							}}
							key={index}
						>
							<Link
								className={classNames(styles.mainLink, { [styles.isActive]: isActive })}
								to={
									item?.internalLink ??
									(item.externalLink && isTrulyExternal(item.externalLink)
										? undefined
										: item.externalLink)
								}
								linkUrl={
									item?.externalLink &&
									item?.externalLink.length > 0 &&
									isTrulyExternal(item.externalLink)
										? item.externalLink
										: undefined
								}
								onKeyDown={(e): void => {
									handleEnterPress(e, index);
								}}
								onKeyUp={handleTabbing}
								onTouchEnd={(e): void => {
									handleTouch(e, index, item.route);
								}}
								weight={'bold'}
								textLink
								aria-haspopup={item.children && item.children.length > 0 ? 'true' : undefined}
								aria-expanded={isActive}
								aria-controls={ariaControlId}
								role={'menuitem'}
								target={calculateTarget(item.externalLink)}
								hideExternalIcon
							>
								{item.name}
							</Link>

							{item.children && item.children.length > 0 && (
								<MenuDropdown
									items={item.children}
									showDropdown={showDropdown === index}
									ariaControlId={ariaControlId}
								/>
							)}
						</li>
					);
				})}
			</ul>
		</nav>
	);
};
